import { useCallback, useState } from "react";
import queue from "queue";
import { SasResponse } from "./filestore";
import { axiosBeyonClient } from "utils/beyonAuth/beyonAxiosClient";
import { useWorkspace } from "./useWorkspace";
import { BlockBlobClient } from "@azure/storage-blob";


export type UploadFileCallbacks = {
    onStart?: () => void,
    onProgress?: (percent: number) => void,
    onComplete?: (fileId: string) => void,
}

export default function useFileUploader(workspaceId: string, concurrency = 200) {

    const [uploadQueue,] = useState(new queue({ concurrency, autostart: true }));

    const workspace = useWorkspace(workspaceId);
    const workspaceConfig = workspace.data?.config;

    const uploadFile = useCallback(uploadFileMemo, [workspaceConfig, uploadQueue]);

    return { uploadFile };

    function uploadFileMemo(file: File, { onStart = () => { }, onProgress = () => { }, onComplete = () => { } }: UploadFileCallbacks) {

        const filestoreEndpoint = workspaceConfig?.filestoreConfig?.apiEndpoint;

        // add the file to the queue
        uploadQueue.push(async () => {
            onStart();

            try {
                // get a sas token from the filestore
                const sasRequest = await axiosBeyonClient.get<SasResponse>(`${filestoreEndpoint}/file/sas`);
                if (!(sasRequest.status === 200 || sasRequest.status === 201)) {
                    throw new Error(sasRequest.statusText);
                }

                // upload the file to the filestore
                const blockBlobClient = new BlockBlobClient(sasRequest.data.sasUri);

                const encodedFilename = encodeURIComponent(file.name ?? Math.random().toString(36).slice(2));

                await blockBlobClient.uploadData(file, {
                    onProgress: (progress: any) => {
                        const percent = progress.loadedBytes * 100 / file.size;
                        onProgress(percent);
                    },
                    blobHTTPHeaders: {
                        blobContentType: file.type,
                        blobContentDisposition: `attachment; filename="${encodedFilename}"`,
                    },
                });

                onComplete(sasRequest.data.fileId);
            } catch (error) {
                throw new Error(`An error occurred while uploading the file ${file.name}: ${error}`);
            }
        });
    }
}
